import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BarChart3,
  ChevronDown,
  Clock,
  Download,
  FileImage,
  FileSpreadsheet,
  FileText,
  Filter,
  RefreshCw,
  Settings,
  Target,
  Users,
  DollarSign,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  useTimeEntries,
  useProjects,
  useTasks,
  useAnalytics,
  useReportTemplates,
  useScheduledReports,
  useReportBenchmarks,
  useCreateReportSchedule,
  useCancelReportSchedule,
  useActiveTeamMembers,
  useSystemSettings,
} from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import { useAuth } from '../contexts/AuthContext';
import { toUTCPlus5Time } from '../utils/time';
import { Button, EmptyState, Panel, ViewTabs } from './ui';
import ExecutiveDashboard from './reports/ExecutiveDashboard';
import FinancialReports from './reports/FinancialReports';
import TeamPerformanceDashboard from './reports/TeamPerformanceDashboard';
import ProjectHealthDashboard from './reports/ProjectHealthDashboard';
import MemberProductivityDashboard from './reports/MemberProductivityDashboard';
import ProjectLeadStatusDashboard from './reports/ProjectLeadStatusDashboard';
import CustomReportsDashboard from './reports/CustomReportsDashboard';
import ScheduleReportsPanel from './reports/ScheduleReportsPanel';
import {
  calculateKPIs,
  calculateProjectMetrics,
  calculateTeamMetrics,
  calculateDailyTrends,
  calculateTrendDelta,
  resolveDateRange,
  previousPeriodBounds,
  sumEntryHours,
  sumRevenue,
  getAvailableViews,
  formatTime,
  formatHours,
} from './reports/utils';

const VIEW_ICONS = {
  executive: BarChart3,
  financial: DollarSign,
  'team-performance': Users,
  'project-health': Target,
  member: Clock,
  'project-status': Target,
  custom: Settings,
};

const ReportsPage = () => {
  const { user } = useAuth();
  const { data: systemSettings } = useSystemSettings();
  const weeklyTargetHours = systemSettings?.weekly_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.weekly_target_hours;
  const currencyCode = systemSettings?.currency_code ?? SYSTEM_SETTINGS_DEFAULTS.currency_code;
  const defaultView = user?.role === 'admin' ? 'executive' : 'member';

  const [selectedView, setSelectedView] = useState(defaultView);
  const [dateRange, setDateRange] = useState('month');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTeamMember, setSelectedTeamMember] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [selectedReportComponents, setSelectedReportComponents] = useState([]);
  const [reportName, setReportName] = useState('');
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  useEffect(() => {
    if (dateRange === 'custom') return;
    const { start, end } = resolveDateRange(dateRange);
    setStartDate(start);
    setEndDate(end);
  }, [dateRange]);

  useEffect(() => {
    const views = getAvailableViews(user?.role);
    if (!views.find((v) => v.id === selectedView)) {
      setSelectedView(views[0]?.id || defaultView);
    }
  }, [user?.role, selectedView, defaultView]);

  const scopedUserId = useMemo(() => {
    if (user?.role === 'member') return user.id;
    if (selectedView === 'member' && user?.id) return user.id;
    return selectedTeamMember || '';
  }, [user, selectedView, selectedTeamMember]);

  const entryFilters = useMemo(
    () => ({
      page_size: 200,
      ...(selectedProject ? { project_id: selectedProject } : {}),
      ...(scopedUserId ? { user_id: scopedUserId } : {}),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    }),
    [selectedProject, scopedUserId, startDate, endDate]
  );

  const { prevStart, prevEnd } = useMemo(
    () => previousPeriodBounds(startDate, endDate),
    [startDate, endDate]
  );

  const prevFilters = useMemo(
    () => ({
      page_size: 200,
      ...(selectedProject ? { project_id: selectedProject } : {}),
      ...(scopedUserId ? { user_id: scopedUserId } : {}),
      ...(prevStart ? { start_date: prevStart } : {}),
      ...(prevEnd ? { end_date: prevEnd } : {}),
    }),
    [selectedProject, scopedUserId, prevStart, prevEnd]
  );

  const timeEntriesQuery = useTimeEntries(entryFilters);
  const prevEntriesQuery = useTimeEntries(prevFilters);
  const projectsQuery = useProjects();
  const tasksQuery = useTasks({ page_size: 200 });
  const canViewTeam = user?.role === 'admin' || user?.role === 'project_lead';
  const teamQuery = useActiveTeamMembers({ enabled: canViewTeam });
  const analyticsQuery = useAnalytics(
    dateRange === 'custom' ? 'custom' : dateRange,
    dateRange === 'custom' ? { startDate, endDate } : null
  );
  const templatesQuery = useReportTemplates();
  const schedulesQuery = useScheduledReports();
  const benchmarksQuery = useReportBenchmarks();
  const createSchedule = useCreateReportSchedule();
  const cancelSchedule = useCancelReportSchedule();

  const timeEntries = timeEntriesQuery.data?.results || timeEntriesQuery.data || [];
  const prevEntries = prevEntriesQuery.data?.results || prevEntriesQuery.data || [];
  const projects = projectsQuery.data?.results || projectsQuery.data || [];
  const tasks = tasksQuery.data?.results || tasksQuery.data || [];
  const teamMembers = Array.isArray(teamQuery.data)
    ? teamQuery.data
    : teamQuery.data?.results || (user ? [user] : []);
  const analytics = analyticsQuery.data || null;
  const templates = templatesQuery.data?.templates || [];
  const schedules = schedulesQuery.data?.schedules || [];
  const benchmarks = benchmarksQuery.data?.benchmarks || null;

  const loading =
    timeEntriesQuery.isLoading ||
    projectsQuery.isLoading ||
    tasksQuery.isLoading ||
    teamQuery.isLoading;

  const refreshing =
    timeEntriesQuery.isFetching ||
    projectsQuery.isFetching ||
    tasksQuery.isFetching ||
    analyticsQuery.isFetching;

  const kpis = useMemo(
    () => calculateKPIs({ timeEntries, projects, tasks, teamMembers, analytics, weeklyTargetHours }),
    [timeEntries, projects, tasks, teamMembers, analytics, weeklyTargetHours]
  );
  const projectMetrics = useMemo(
    () => calculateProjectMetrics(projects, timeEntries, tasks),
    [projects, timeEntries, tasks]
  );
  const teamMetrics = useMemo(
    () => calculateTeamMetrics(teamMembers, timeEntries, tasks, startDate),
    [teamMembers, timeEntries, tasks, startDate]
  );
  const dailyTrends = useMemo(
    () => calculateDailyTrends(timeEntries, startDate, endDate),
    [timeEntries, startDate, endDate]
  );

  const trends = useMemo(() => {
    const billablePct = (entries) => {
      const total = sumEntryHours(entries);
      if (!total) return 0;
      const billable =
        entries
          .filter((e) => e.is_billable)
          .reduce((s, e) => s + (e.duration || 0), 0) / 3600;
      return (billable / total) * 100;
    };
    return {
      hours: calculateTrendDelta(sumEntryHours(timeEntries), sumEntryHours(prevEntries)),
      revenue: calculateTrendDelta(
        sumRevenue(timeEntries, projects),
        sumRevenue(prevEntries, projects)
      ),
      billable: calculateTrendDelta(billablePct(timeEntries), billablePct(prevEntries)),
    };
  }, [timeEntries, prevEntries, projects]);

  const availableViews = useMemo(
    () =>
      getAvailableViews(user?.role).map((view) => ({
        ...view,
        icon: VIEW_ICONS[view.id],
      })),
    [user?.role]
  );

  const refreshAll = () => {
    timeEntriesQuery.refetch();
    prevEntriesQuery.refetch();
    projectsQuery.refetch();
    tasksQuery.refetch();
    teamQuery.refetch();
    analyticsQuery.refetch();
    benchmarksQuery.refetch();
    schedulesQuery.refetch();
  };

  const addComponentToReport = (componentType) => {
    setSelectedReportComponents((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, type: componentType, addedAt: new Date().toISOString() },
    ]);
  };

  const removeComponentFromReport = (componentId) => {
    setSelectedReportComponents((prev) => prev.filter((comp) => comp.id !== componentId));
  };

  const clearReport = () => {
    setSelectedReportComponents([]);
    setReportName('');
    setShowReportPreview(false);
  };

  const exportReport = () => {
    const reportData = {
      name: reportName || 'Custom Report',
      components: selectedReportComponents,
      filters: { dateRange, selectedProject, selectedTeamMember, startDate, endDate },
      generatedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName || 'custom-report'}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully!');
  };

  const exportToCSV = async () => {
    setExportingCSV(true);
    try {
      const rows = timeEntries.map((entry) => ({
        Date: entry.start_time ? format(new Date(entry.start_time), 'yyyy-MM-dd') : '',
        'Start Time': entry.start_time ? toUTCPlus5Time(entry.start_time) : '',
        'End Time': entry.end_time ? toUTCPlus5Time(entry.end_time) : 'Running',
        Description: entry.description || '',
        Duration: formatTime(entry.duration || 0),
        'Hours (Decimal)': formatHours(entry.duration || 0),
        Project: projects.find((p) => p.id === entry.project_id)?.name || 'Unknown',
        Billable: entry.is_billable ? 'Yes' : 'No',
      }));
      const csvContent = [
        Object.keys(rows[0] || { Date: '' }).join(','),
        ...rows.map((row) => Object.values(row).map((val) => `"${val}"`).join(',')),
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-report-${startDate || 'all'}-to-${endDate || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV report exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV report');
    } finally {
      setExportingCSV(false);
    }
  };

  const exportToExcel = async () => {
    setExportingExcel(true);
    try {
      const filename = `project-metrics-${startDate || 'all'}-to-${endDate || 'all'}.xlsx`;
      const response = await axios.post(
        '/api/analytics/export/excel',
        { data: projectMetrics, filename },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel report exported successfully');
    } catch (error) {
      toast.error('Failed to export Excel report');
    } finally {
      setExportingExcel(false);
    }
  };

  const exportToPDF = async (reportType) => {
    setExportingPDF(true);
    try {
      const response = await axios.post(
        '/api/reports/export/pdf',
        {
          reportType: reportType === 'custom' ? 'summary' : reportType,
          dateRange,
          startDate,
          endDate,
          selectedProject,
          selectedTeamMember,
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${String(reportType).replace('-', '_')}_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Professional PDF report exported successfully!');
    } catch (error) {
      toast.error('Failed to export PDF report');
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="reports-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="page-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-secondary-900">
              {user?.role === 'member' ? 'My Reports' : 'Advanced Reports'}
            </h1>
            <p className="text-secondary-600 mt-1">
              Comprehensive insights and analytics for{' '}
              {user?.role === 'admin'
                ? 'executive decision making'
                : user?.role === 'project_lead'
                  ? 'team management'
                  : 'personal productivity'}
            </p>
          </div>
          <div className="flex items-center gap-3 ml-6">
            <Button variant="secondary" onClick={refreshAll} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <div className="relative group">
              <Button variant="primary">
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-4 w-4" />
              </Button>
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-2">
                  <button
                    type="button"
                    onClick={exportToCSV}
                    disabled={exportingCSV}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 flex items-center gap-2"
                    data-testid="export-csv"
                  >
                    <FileText className="h-4 w-4" />
                    CSV Report
                  </button>
                  <button
                    type="button"
                    onClick={exportToExcel}
                    disabled={exportingExcel}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 flex items-center gap-2"
                    data-testid="export-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel Report
                  </button>
                  <button
                    type="button"
                    onClick={() => exportToPDF(selectedView)}
                    disabled={exportingPDF}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 flex items-center gap-2"
                    data-testid="export-pdf"
                  >
                    <FileImage className="h-4 w-4" />
                    PDF Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Panel>
        <ViewTabs tabs={availableViews} activeId={selectedView} onChange={setSelectedView} />
      </Panel>

      <Panel>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <select
              className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              data-testid="reports-date-range"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="last7">Last 7 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
            <select
              className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              data-testid="reports-project-filter"
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {(user?.role === 'admin' || user?.role === 'project_lead') && (
              <select
                className="w-full px-3 py-2 border border-secondary-300 rounded-md text-sm"
                value={selectedTeamMember}
                onChange={(e) => setSelectedTeamMember(e.target.value)}
                data-testid="reports-member-filter"
              >
                <option value="">All Members</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.full_name || member.username}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center gap-1 px-3 py-2 text-sm text-secondary-600"
            >
              <Settings className="h-4 w-4" />
              Advanced
            </button>
            <Button variant="primary" onClick={refreshAll} disabled={refreshing}>
              <Filter className="h-4 w-4" />
              Apply
            </Button>
          </div>
        </div>

        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="reports-start-date"
                />
                <input
                  type="date"
                  className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="reports-end-date"
                />
              </>
            )}
            {user?.role === 'admin' && (
              <label className="flex items-center gap-2 text-sm text-secondary-700">
                <input
                  type="checkbox"
                  checked={showPredictions}
                  onChange={(e) => setShowPredictions(e.target.checked)}
                />
                Predictions
              </label>
            )}
          </div>
        )}
      </Panel>

      {!timeEntries.length && !projects.length ? (
        <EmptyState
          title="No report data yet"
          description="Log time entries and create projects to populate reports."
        />
      ) : null}

      {selectedView === 'executive' && user?.role === 'admin' && (
        <ExecutiveDashboard
          kpis={kpis}
          projectMetrics={projectMetrics}
          teamMetrics={teamMetrics}
          trends={trends}
          benchmarks={benchmarks}
          showPredictions={showPredictions}
          currencyCode={currencyCode}
        />
      )}
      {selectedView === 'financial' && user?.role === 'admin' && (
        <FinancialReports kpis={kpis} projectMetrics={projectMetrics} trends={trends} currencyCode={currencyCode} />
      )}
      {selectedView === 'team-performance' && user?.role === 'admin' && (
        <TeamPerformanceDashboard teamMetrics={teamMetrics} />
      )}
      {selectedView === 'project-health' && user?.role === 'admin' && (
        <ProjectHealthDashboard projectMetrics={projectMetrics} currencyCode={currencyCode} />
      )}
      {selectedView === 'member' && (user?.role === 'member' || user?.role === 'project_lead') && (
        <MemberProductivityDashboard
          kpis={kpis}
          dailyTrends={dailyTrends}
          timeEntries={timeEntries}
          projects={projects}
          trends={trends}
        />
      )}
      {selectedView === 'project-status' && user?.role === 'project_lead' && (
        <ProjectLeadStatusDashboard
          projects={projects}
          tasks={tasks}
          timeEntries={timeEntries}
          currentUserId={user?.id}
        />
      )}
      {selectedView === 'custom' && (
        <CustomReportsDashboard
          kpis={kpis}
          projectMetrics={projectMetrics}
          teamMetrics={teamMetrics}
          dailyTrends={dailyTrends}
          timeEntries={timeEntries}
          currencyCode={currencyCode}
          selectedReportComponents={selectedReportComponents}
          reportName={reportName}
          setReportName={setReportName}
          showReportPreview={showReportPreview}
          setShowReportPreview={setShowReportPreview}
          addComponentToReport={addComponentToReport}
          removeComponentFromReport={removeComponentFromReport}
          clearReport={clearReport}
          exportReport={exportReport}
          user={user}
        />
      )}

      {user?.role === 'admin' && (
        <ScheduleReportsPanel
          templates={templates}
          schedules={schedules}
          canManage
          isCreating={createSchedule.isPending}
          onCreate={(payload) => createSchedule.mutateAsync(payload)}
          onCancel={(id) => cancelSchedule.mutate(id)}
        />
      )}
    </div>
  );
};

export default ReportsPage;
